import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PaymentStatus } from '@cookspace-shared-contracts/core';
import { Eye, RotateCcw } from 'lucide-react';

import { useListPaymentsQuery, useRefundPaymentMutation } from '@/api/paymentsApi';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import type { AdminPaymentListItem } from '@/lib/paymentsSchemas';

const STATUS_OPTIONS: Array<{ value: PaymentStatus; label: string }> = [
    { value: 'pending', label: 'Ожидает' },
    { value: 'succeeded', label: 'Оплачен' },
    { value: 'cancelled', label: 'Отменён' },
    { value: 'failed', label: 'Ошибка' },
    { value: 'refunded', label: 'Возврат' }
];

const STATUS_LABEL: Record<PaymentStatus, string> = {
    pending: 'Ожидает',
    succeeded: 'Оплачен',
    cancelled: 'Отменён',
    failed: 'Ошибка',
    refunded: 'Возврат'
};

const STATUS_COLOR: Record<PaymentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    succeeded: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-blue-100 text-blue-800'
};

const PRICE_FORMATTER = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
});

const PAGE_SIZE = 20;
const REASON_MIN_LENGTH = 10;

function formatPrice(rub: number): string {
    return PRICE_FORMATTER.format(rub);
}

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleString('ru-RU');
}

function formatUserContact(p: AdminPaymentListItem): string {
    const { user } = p;
    if (user === null) {
        return p.userId.slice(0, 8);
    }
    if (user.email !== null && user.email !== '') {
        return user.email;
    }
    if (user.phoneNumber !== null && user.phoneNumber !== '') {
        return user.phoneNumber;
    }
    return user.displayName ?? user.id.slice(0, 8);
}

function isRefundable(p: AdminPaymentListItem): boolean {
    if (p.status !== 'succeeded') {
        return false;
    }
    if (p.refundedAmountRub !== null && p.refundedAmountRub >= p.amountRub) {
        return false;
    }
    return true;
}

interface RefundIntent {
    payment: AdminPaymentListItem;
}

export function PaymentsPage(): ReactElement {
    const navigate = useNavigate();

    const [filterStatus, setFilterStatus] = useState<PaymentStatus | ''>('');
    const [searchInput, setSearchInput] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(searchValue !== '' ? { search: searchValue } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [filterStatus, searchValue, page]
    );

    const { data, isLoading, isError } = useListPaymentsQuery(queryArg);
    const [refundPayment, { isLoading: isRefunding }] = useRefundPaymentMutation();

    const [refundIntent, setRefundIntent] = useState<RefundIntent | null>(null);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');
    const [refundAmountError, setRefundAmountError] = useState<string | null>(null);
    const [refundReasonError, setRefundReasonError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    const handleSearchSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setSearchValue(searchInput.trim());
        setPage(0);
    };

    const openRefund = (payment: AdminPaymentListItem): void => {
        setRefundIntent({ payment });
        setRefundAmount('');
        setRefundReason('');
        setRefundAmountError(null);
        setRefundReasonError(null);
    };

    const closeRefund = (): void => {
        setRefundIntent(null);
        setRefundAmount('');
        setRefundReason('');
        setRefundAmountError(null);
        setRefundReasonError(null);
    };

    const submitRefund = async (): Promise<void> => {
        if (!refundIntent) {
            return;
        }
        const { payment } = refundIntent;

        const reasonTrimmed = refundReason.trim();
        let hasError = false;

        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setRefundReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            hasError = true;
        } else {
            setRefundReasonError(null);
        }

        const refundedSoFar = payment.refundedAmountRub ?? 0;
        const maxRefund = payment.amountRub - refundedSoFar;

        let amount: number | undefined;
        if (refundAmount.trim() !== '') {
            const parsed = Number(refundAmount);
            if (!Number.isInteger(parsed) || parsed < 1 || parsed > maxRefund) {
                setRefundAmountError(`Сумма от 1 до ${maxRefund} ₽`);
                hasError = true;
            } else {
                setRefundAmountError(null);
                amount = parsed;
            }
        } else {
            setRefundAmountError(null);
        }

        if (hasError) {
            return;
        }

        try {
            await refundPayment({
                id: payment.id,
                body: {
                    reason: reasonTrimmed,
                    ...(amount !== undefined ? { amount } : {})
                }
            }).unwrap();
            closeRefund();
        } catch {
            setPageError('Не удалось оформить возврат');
            closeRefund();
        }
    };

    return (
        <div data-testid="payments-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Платежи</h1>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value as PaymentStatus | '');
                            setPage(0);
                        }}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="payments-filter-status"
                    />
                </div>
                <form
                    onSubmit={handleSearchSubmit}
                    className="flex w-72 items-end gap-2"
                    data-testid="payments-search-form"
                >
                    <div className="flex-1">
                        <Input
                            label="Поиск (email/телефон)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="user@mail.ru"
                            data-testid="payments-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                        data-testid="payments-search-submit"
                    >
                        Найти
                    </button>
                </form>
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="payments-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="payments-error">
                    Не удалось загрузить платежи
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="payments-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,1.4fr)_120px_120px_140px_120px_140px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Пользователь</span>
                        <span>Сумма</span>
                        <span>Статус</span>
                        <span>Оплачен</span>
                        <span>Возврат</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Платежи не найдены
                        </div>
                    )}

                    {items.map((p) => (
                        <div
                            key={p.id}
                            className="grid grid-cols-[80px_minmax(0,1.4fr)_120px_120px_140px_120px_140px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`payment-row-${p.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {p.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">
                                {formatUserContact(p)}
                            </span>
                            <span className="text-gray-900">{formatPrice(p.amountRub)}</span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[p.status]}`}
                                >
                                    {STATUS_LABEL[p.status]}
                                </span>
                            </span>
                            <span className="text-xs text-gray-700">{formatDate(p.paidAt)}</span>
                            <span className="text-xs text-gray-700">
                                {p.refundedAmountRub !== null
                                    ? formatPrice(p.refundedAmountRub)
                                    : '—'}
                            </span>
                            <span className="flex justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/payments/${p.id}`)}
                                    data-testid={`payment-view-${p.id}`}
                                    className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                    aria-label="Просмотр"
                                    title="Просмотр"
                                >
                                    <Eye size={14} />
                                </button>
                                {isRefundable(p) && (
                                    <button
                                        type="button"
                                        onClick={() => openRefund(p)}
                                        data-testid={`payment-refund-${p.id}`}
                                        className="inline-flex items-center gap-1 rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-50"
                                    >
                                        <RotateCcw size={12} />
                                        Возврат
                                    </button>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="payments-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="payments-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="payments-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}

            <Modal
                isOpen={refundIntent !== null}
                onClose={closeRefund}
                title="Оформить возврат"
                testId="payment-refund-modal"
            >
                {refundIntent !== null && (
                    <div className="space-y-4" data-testid="payment-refund-form">
                        <p className="text-sm text-gray-700">
                            Платёж{' '}
                            <span className="font-medium">
                                {formatPrice(refundIntent.payment.amountRub)}
                            </span>{' '}
                            пользователя{' '}
                            <span className="font-medium">
                                {formatUserContact(refundIntent.payment)}
                            </span>
                            .
                            {refundIntent.payment.refundedAmountRub !== null && (
                                <span className="block text-xs text-gray-600">
                                    Уже возвращено:{' '}
                                    {formatPrice(refundIntent.payment.refundedAmountRub)}
                                </span>
                            )}
                        </p>

                        <Input
                            label={`Сумма возврата (₽), пусто = полный возврат (макс ${
                                refundIntent.payment.amountRub -
                                (refundIntent.payment.refundedAmountRub ?? 0)
                            } ₽)`}
                            type="number"
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            placeholder="Полный возврат"
                            error={refundAmountError ?? undefined}
                            data-testid="payment-refund-amount"
                        />

                        <Textarea
                            label={`Причина (минимум ${REASON_MIN_LENGTH} символов)`}
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            rows={4}
                            error={refundReasonError ?? undefined}
                            data-testid="payment-refund-reason"
                            autoFocus
                        />

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={closeRefund}
                                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    void submitRefund();
                                }}
                                disabled={isRefunding}
                                data-testid="payment-refund-submit"
                                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                            >
                                Оформить возврат
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
